import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Layout } from "./components/layout/Layout";
import { LoginPage } from "./components/pages/LoginPage";
import { DashboardPage } from "./components/pages/DashboardPage";
import { ClientesPage } from "./components/pages/ClientesPage";
import { ProdutosPage } from "./components/pages/ProdutosPage";
import { NotasFiscaisPage } from "./components/pages/NotasFiscaisPage";
import { ContasReceberPage } from "./components/pages/ContasReceberPage";
import { ContasPagarPage } from "./components/pages/ContasPagarPage";
import { ContasBancariasPage } from "./components/pages/ContasBancariasPage";
import { AtividadesPage } from "./components/pages/AtividadesPage";
import { ImportacoesPage } from "./components/pages/ImportacoesPage";
import { FornecedoresPage } from "./components/pages/FornecedoresPage";
import { EstoquePage } from "./components/pages/EstoquePage";
import { ConcorrentesPage } from "./components/pages/ConcorrentesPage";
import { InteligenciaMercadoPage } from "./components/pages/InteligenciaMercadoPage";
import { ConfiguracoesPage } from "./components/pages/ConfiguracoesPage";
import { RelatoriosPage } from "./components/pages/RelatoriosPage";
import { AuditoriaPage } from "./components/pages/AuditoriaPage";
import { OutrasEmpresasPage } from "./components/pages/OutrasEmpresasPage";
import { PetronectPage } from "./components/pages/PetronectPage";
import { Loader2 } from "lucide-react";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#ea580c]" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#ea580c]" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function OutrasEmpresasRoute({ children }) {
  const { user, loading, canAccessOutrasEmpresas } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#ea580c]" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!canAccessOutrasEmpresas()) {
    return <Navigate to="/" replace />;
  }

  return <Layout>{children}</Layout>;
}

function PetronectRoute({ children }) {
  const { user, loading, canViewPetronect } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#ea580c]" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!canViewPetronect()) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Protected Routes - Financeiro */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notas-fiscais"
        element={
          <ProtectedRoute>
            <NotasFiscaisPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contas-receber"
        element={
          <ProtectedRoute>
            <ContasReceberPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contas-pagar"
        element={
          <ProtectedRoute>
            <ContasPagarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contas-bancarias"
        element={
          <ProtectedRoute>
            <ContasBancariasPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/relatorios"
        element={
          <ProtectedRoute>
            <RelatoriosPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/outras-empresas"
        element={
          <OutrasEmpresasRoute>
            <OutrasEmpresasPage />
          </OutrasEmpresasRoute>
        }
      />
      <Route path="/petronect" element={<PetronectRoute><PetronectPage /></PetronectRoute>} />

      {/* Protected Routes - Operacional */}
      <Route
        path="/atividades"
        element={
          <ProtectedRoute>
            <AtividadesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/importacoes"
        element={
          <ProtectedRoute>
            <ImportacoesPage />
          </ProtectedRoute>
        }
      />

      {/* Protected Routes - Portfolio */}
      <Route
        path="/produtos"
        element={
          <ProtectedRoute>
            <ProdutosPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/estoque"
        element={
          <ProtectedRoute>
            <EstoquePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/fornecedores"
        element={
          <ProtectedRoute>
            <FornecedoresPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/concorrentes"
        element={
          <ProtectedRoute>
            <ConcorrentesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inteligencia-mercado"
        element={
          <ProtectedRoute>
            <InteligenciaMercadoPage />
          </ProtectedRoute>
        }
      />

      {/* Protected Routes - Cadastros */}
      <Route
        path="/clientes"
        element={
          <ProtectedRoute>
            <ClientesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/configuracoes"
        element={
          <ProtectedRoute>
            <ConfiguracoesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/auditoria"
        element={
          <ProtectedRoute>
            <AuditoriaPage />
          </ProtectedRoute>
        }
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
